import {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { selectCurrentIdentityLanguage } from 'routes/AppContainer/selectors';
import { queryRequest } from 'utils/queryRequest';
import { ensurePayloadAndQuery } from 'utils/ensurePayloadAndQuery';
import { GetterApiEndpointNames } from 'new-components/DLS/OpDataFetchTable/types';

interface WithParameters<
  T extends GetterApiEndpointNames,
  SelectData = InfiniteData<Awaited<ReturnType<Api.Client[T]>>, unknown>,
> extends Omit<
    UseInfiniteQueryOptions<
      Awaited<ReturnType<Api.Client[T]>>,
      OP.ApiError,
      SelectData
    >,
    'queryKey' | 'getNextPageParam' | 'initialPageParam'
  > {
  apiEndpointName: T;
  parameters: Parameters<Api.Client[T]>;
  suppressErrorMessage?: boolean;
  getAll?: boolean;
  queryKey?: QueryKey;
}

interface WithOptionalParameters<
  T extends GetterApiEndpointNames,
  SelectData = InfiniteData<Awaited<ReturnType<Api.Client[T]>>, unknown>,
> extends Omit<
    UseInfiniteQueryOptions<
      Awaited<ReturnType<Api.Client[T]>>,
      OP.ApiError,
      SelectData
    >,
    'queryKey' | 'getNextPageParam' | 'initialPageParam'
  > {
  apiEndpointName: T;
  parameters?: Parameters<Api.Client[T]>;
  suppressErrorMessage?: boolean;
  getAll?: boolean;
  queryKey?: QueryKey;
}

export type UseOpInfiniteQueryOptions<
  T extends GetterApiEndpointNames = GetterApiEndpointNames,
  SelectData = InfiniteData<Awaited<ReturnType<Api.Client[T]>>, unknown>,
> = Api.ClientSpec[T]['pathParams'] extends []
  ? WithOptionalParameters<T, SelectData>
  : WithParameters<T, SelectData>;

/**
 * This infinite version of query should only be used with cursors
 * @see {@link useOpPaginatedQuery}
 */
export const useOpInfiniteQuery = <
  T extends GetterApiEndpointNames,
  SelectData = InfiniteData<Awaited<ReturnType<Api.Client[T]>>, unknown>,
>({
  apiEndpointName,
  parameters,
  suppressErrorMessage = false,
  queryKey = [apiEndpointName, JSON.stringify(parameters)],
  ...queryOptions
}: UseOpInfiniteQueryOptions<T, SelectData>) => {
  const { parametersArray, withQuery } = ensurePayloadAndQuery(
    apiEndpointName,
    parameters,
  );

  // TODO - get the language based on authed page logic if not in identity
  /** we first look at the identity language and set the language to that.
   * If that is not set, we then fallback to the local storage setting.
   * If that is not set, then we fallback to the browser locale.
   * And if that is not supported then we use English (en). */
  const currentIdentityLanguage = useSelector(selectCurrentIdentityLanguage());

  // Find the index of the queries (last object)
  const queriesIndex = parametersArray.findLastIndex(
    (param) => typeof param === 'object',
  );

  // Ensure the requirements have been passed if the enabled option is false
  const hasValidRequirements = parametersArray.every(Boolean);

  return useInfiniteQuery<
    Awaited<ReturnType<Api.Client[T]>>,
    OP.ApiError,
    SelectData
  >({
    queryKey,
    queryFn: async ({ pageParam }) => {
      if (withQuery) {
        if (queriesIndex === -1) {
          // ensurePayloadAndQuery guarantees the presence of a queries object, so throw since something is way off...
          throw new Error(
            `Critical: ensurePayloadAndQuery did not ensure a query object for API endpoint ${apiEndpointName}`,
          );
        }

        if (
          typeof parametersArray[queriesIndex] === 'object' &&
          parametersArray[queriesIndex] !== null &&
          pageParam
        ) {
          // @ts-expect-error - cursor can exist in this case
          parametersArray[queriesIndex]!.cursor = pageParam;
        }
      }

      const result = await queryRequest({
        apiEndpointName,
        parameters: parametersArray,
        currentIdentityLanguage,
      });

      if (result.statusCode >= 400) {
        if (suppressErrorMessage) {
          console.error(`Error during ${apiEndpointName}`, { result });
        } else {
          throw result.json;
        }
      }

      return result;
    },
    getNextPageParam: (lastPage) => lastPage?.json?.cursors?.nextCursor,
    initialPageParam: undefined,
    enabled: hasValidRequirements,
    ...queryOptions,
  });
};
